import React from "react";
import InputMask from "react-input-mask";
import {
  TextField,
  Autocomplete,
  Button,
  Typography,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

function getEmailHelperText(checking, exists) {
  if (checking) return "Checking email...";
  if (exists) return "This email already exists.";
  return "";
}

/**
 * Shared law-firm user form fields used by UsersStep and AddLawFirmUser.
 */
export default function LawFirmUserFormFields({
  index,
  user,
  handleUserInputChange,
  locations,
  positionOptions,
  lawSchools,
  legalServiceProfessionals,
  emailExists = false,
  emailChecking = false,
  showViewerPermission = true,
}) {
  return (
    <>
      <Grid size={{ xs: 12 }}>
        <FormControl fullWidth required>
          <InputLabel>Law Firm Location</InputLabel>
          <Select
            value={user.location}
            onChange={(e) => handleUserInputChange(index, "location", e.target.value)}
            label="Law Firm Location"
          >
            <MenuItem value="" disabled>
              Select location
            </MenuItem>
            {locations.map((location) => (
              <MenuItem
                key={location.law_firm_location_id}
                value={location.law_firm_location_id}
              >
                {location.name} - {location.address}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid size={{ xs: 12, sm: 12 }}>
        <FormControl fullWidth required>
          <InputLabel>Position</InputLabel>
          <Select
            value={user.position}
            onChange={(e) => handleUserInputChange(index, "position", e.target.value)}
            label="Position"
          >
            <MenuItem value="">Select Position</MenuItem>
            {positionOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {user.position === "Attorney" && (
        <>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              options={lawSchools}
              getOptionLabel={(option) => option.name || ""}
              value={
                lawSchools.find((school) => school.lawschool_id === user.lawschool) || null
              }
              onChange={(event, newValue) =>
                handleUserInputChange(
                  index,
                  "lawschool",
                  newValue ? newValue.lawschool_id : ""
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Law School"
                  variant="outlined"
                  fullWidth
                  required
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Bar Number"
              fullWidth
              value={user.barNumber}
              required
              onChange={(e) => handleUserInputChange(index, "barNumber", e.target.value)}
            />
          </Grid>
        </>
      )}

      {user.position === "Paralegal" && !user.showCustomForm && (
        <Grid size={{ xs: 12 }}>
          <FormControl component="fieldset">
            <Typography variant="subtitle1">
              Is this individual an Independent Paralegal who works for multiple law firms?
            </Typography>
            <RadioGroup
              row
              value={user.isLegalService ? "yes" : "no"}
              onChange={(e) =>
                handleUserInputChange(index, "isLegalService", e.target.value === "yes")
              }
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>

          {user.isLegalService && (
            <>
              <Select
                fullWidth
                value={user.selectedProfessional}
                onChange={(e) =>
                  handleUserInputChange(index, "selectedProfessional", e.target.value)
                }
                displayEmpty
                sx={{ mt: 2 }}
              >
                <MenuItem value="" disabled>
                  Select Professional...
                </MenuItem>
                {legalServiceProfessionals.map((professional) => (
                  <MenuItem key={professional.id} value={professional.id}>
                    {professional.name}
                  </MenuItem>
                ))}
              </Select>
              <Button
                onClick={() => handleUserInputChange(index, "showCustomForm", true)}
                sx={{ mt: 1 }}
              >
                Legal Service Professional not in list?
              </Button>
            </>
          )}
        </Grid>
      )}

      {(user.showCustomForm || !user.isLegalService || user.position !== "Paralegal") && (
        <Grid container spacing={2} sx={{ mt: 2, ml: 0.2 }}>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              label="Prefix"
              value={user.prefix}
              onChange={(e) => handleUserInputChange(index, "prefix", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              label="First Name"
              value={user.firstName}
              onChange={(e) => handleUserInputChange(index, "firstName", e.target.value)}
              required
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              label="Middle Name"
              value={user.middleName}
              onChange={(e) => handleUserInputChange(index, "middleName", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              label="Last Name"
              value={user.lastName}
              onChange={(e) => handleUserInputChange(index, "lastName", e.target.value)}
              required
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Suffix"
              value={user.suffix}
              onChange={(e) => handleUserInputChange(index, "suffix", e.target.value)}
              select
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Jr.">Jr.</MenuItem>
              <MenuItem value="Sr.">Sr.</MenuItem>
              <MenuItem value="II">I</MenuItem>
              <MenuItem value="II">II</MenuItem>
              <MenuItem value="III">III</MenuItem>
              <MenuItem value="IV">IV</MenuItem>
              <MenuItem value="V">V</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Email"
              type="email"
              value={user.email}
              onChange={(e) => handleUserInputChange(index, "email", e.target.value)}
              required
              fullWidth
              error={Boolean(emailExists)}
              helperText={getEmailHelperText(emailChecking, emailExists)}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={user.useFirmEmail}
                  onChange={(e) =>
                    handleUserInputChange(index, "useFirmEmail", e.target.checked)
                  }
                />
              }
              label="Use a different email for this firm's communications"
            />
          </Grid>

          {user.useFirmEmail && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Firm-Specific Email"
                type="email"
                value={user.firmEmail}
                onChange={(e) => handleUserInputChange(index, "firmEmail", e.target.value)}
                fullWidth
                helperText="This email will be used for communications related to this firm only. Login will still use the primary email above."
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <InputMask
              mask="(999) 999-9999"
              value={user.phoneNumber}
              onChange={(e) => handleUserInputChange(index, "phoneNumber", e.target.value)}
            >
              {(inputProps) => <TextField {...inputProps} label="Phone Number" fullWidth />}
            </InputMask>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Extension"
              value={user.extension}
              onChange={(e) => handleUserInputChange(index, "extension", e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Permission Level</InputLabel>
              <Select
                value={user.permissionLevel}
                onChange={(e) =>
                  handleUserInputChange(index, "permissionLevel", e.target.value)
                }
                label="Permission Level"
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="User">User</MenuItem>
                {showViewerPermission && <MenuItem value="Viewer">Viewer</MenuItem>}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
    </>
  );
}
